class WispButton extends HTMLElement {
  constructor() {
    super();

    this.wispContainerId = 'wisp_container'; // V1
    this.wisepopsFeedId = 'wisepops-feed'; // V2
  }

  connectedCallback() {
    this.observeClassChanges();
    this.observeBodyChanges();
  }

  observeClassChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          this.toggleWispOpenClass();
        }
      });
    });
    observer.observe(this, { attributes: true });
  }

  toggleWispOpenClass() {
    if (this.classList.contains('wisp--open')) {
      document.body.classList.add('wisp--open');
      this.noScroll(true);
    } else {
      document.body.classList.remove('wisp--open');
      this.noScroll(false);
    }
  }

  observeBodyChanges() {
    const observerBody = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.id === this.wispContainerId) {
            // V1: regular DOM container
            this.addElements();
            observerBody.disconnect();
          } else if (node.id === this.wisepopsFeedId) {
            // V2: Shadow DOM container
            this.addV2Elements(node);
            observerBody.disconnect();
          }
        });
      });
    });

    observerBody.observe(document.body, { childList: true });
  }

  addElements() {
    const wispContainer = document.getElementById(this.wispContainerId);
    if (!wispContainer) return;

    this.addOverlay(wispContainer);
    this.addHeadingAndCloseButton(wispContainer);
  }

  // V2 path — new
  addV2Elements(feedContainer) {
    if (!feedContainer) return;

    // Overlay goes in the light DOM (same approach as V1)
    this.addOverlay(feedContainer);

    // Heading + close go into the shadow root
    const shadowRoot = feedContainer.shadowRoot;
    if (shadowRoot) {
      this.addV2HeadingAndCloseButton(shadowRoot);
    }
  }

  // Shared: insert overlay before container (works in both V1 and V2)
  addOverlay(container) {
    const wispOverlay = document.createElement('div');
    wispOverlay.classList.add(
      'wisp-overlay',
      'hidden',
      'fixed',
      'inset-0',
      'z-50',
      'bg-black',
      'bg-opacity-20',
      'cursor-pointer',
    );
    wispOverlay.style =
      '-webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);' +
      '-webkit-backface-visibility: hidden; backface-visibility: hidden;' +
      '-webkit-perspective: 1000;';
    container.parentNode.insertBefore(wispOverlay, container);
  }

  // V1 path — unchanged (Tailwind classes work in light DOM)
  addHeadingAndCloseButton(wispContainer) {
    const wispHeading = document.createElement('div');
    wispHeading.classList.add('wisp-heading', 'flex', 'justify-between', 'items-center', 'bg-white', 'pl-6', 'pr-2');

    const heading = document.createElement('h2');
    heading.classList.add('text-xs', 'uppercase');
    heading.textContent = window.wispStrings.notificationLabel;

    const closeButton = document.createElement('button');
    closeButton.classList.add('wisp-close-button', 'focus:outline-none', 'p-4');
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <path d="M1.99951 1.99951L13 13" stroke="black"/>
        <path d="M1.99951 13.0005L13 2.00001" stroke="black"/>
      </svg>
    `;

    wispHeading.appendChild(heading);
    wispHeading.appendChild(closeButton);
    wispContainer.prepend(wispHeading);

    closeButton.addEventListener('click', () => {
      this.click();
    });
  }

  // V2 path — new: inject heading into shadow root with inline styles
  // (Tailwind classes are not available inside Shadow DOM)
  addV2HeadingAndCloseButton(shadowRoot) {
    const wispHeading = document.createElement('div');
    wispHeading.classList.add('wisp-heading');

    // Positioned fixed to slide in with the feed panel.
    // Visibility is synced via JS below.
    wispHeading.style.cssText = `
      position: fixed;
      top: 0;
      right: 0;
      width: var(--wp-panel-width, 400px);
      max-width: 100%;
      height: 48px;
      z-index: 2147483647;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
      padding-left: 1.5rem;
      padding-right: 0.5rem;
      transform: translate(100%);
      transition: transform 0.7s;
      box-sizing: border-box;
    `;

    const heading = document.createElement('h2');
    heading.style.cssText = 'font-size: 0.75rem; text-transform: uppercase; margin: 0;';
    heading.textContent = window.wispStrings?.notificationLabel || 'Notifications';

    const closeButton = document.createElement('button');
    closeButton.style.cssText =
      'padding: 1rem; background: none; border: none; cursor: pointer; line-height: 0;';
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <path d="M1.99951 1.99951L13 13" stroke="black"/>
        <path d="M1.99951 13.0005L13 2.00001" stroke="black"/>
      </svg>
    `;

    wispHeading.appendChild(heading);
    wispHeading.appendChild(closeButton);
    shadowRoot.appendChild(wispHeading);

    // Close the feed by clicking the bell
    closeButton.addEventListener('click', () => {
      this.click();
    });

    // Sync heading slide animation with feed open/close state
    const syncVisibility = () => {
      const isOpen = this.classList.contains('wisp--open');
      wispHeading.style.transform = isOpen ? 'translate(0)' : 'translate(100%)';

      // Hide heading on mobile (no CSS media queries available for
      // dynamically created elements in shadow DOM without a stylesheet)
      if (window.innerWidth <= 672) {
        wispHeading.style.display = 'none';
      } else {
        wispHeading.style.display = '';
      }
    };

    const observer = new MutationObserver(syncVisibility);
    observer.observe(this, { attributes: true, attributeFilter: ['class'] });
    syncVisibility();
  }

  // Unchanged methods below
  noScroll(enable) {
    document.documentElement.style.overflow = enable ? 'hidden' : '';
    this.toggleVideos(!enable);
    this.toggleThirdPartyElements(enable);
  }

  toggleVideos(play) {
    play ? window.continuePlayingVideos() : window.pauseVideosPlaying();
  }

  toggleThirdPartyElements(hide) {
    if (hide) {
      document.body.classList.add('hide-third-party-elements');
      return;
    }

    document.body.classList.remove('hide-third-party-elements');
  }
}

customElements.define('wisp-button', WispButton);
